import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const SPECIALIZED_ROLES = [
    {
        id: "technician",
        title: "On-Demand Technician",
        tagline: "Hands-on field execution",
        descriptor:
            "Strike-ready operator. Deploys to active missions, executes audits and remediations, paid per engagement.",
    },
    {
        id: "surgeon",
        title: "Sovereign Surgeon",
        tagline: "Specialist precision work",
        descriptor:
            "Senior specialist invited to high-risk dossiers. Deep-dive vulnerability work, priority bounties, retained engagements.",
    },
    {
        id: "admiral",
        title: "Fleet Admiral",
        tagline: "Sector leadership & residuals",
        descriptor:
            "Builds and leads a sub-fleet under your hull. Recruits Technicians and Surgeons, earns residuals on every placement in your sector.",
    },
];

const COVENANT_REQUIRED_ROLES = new Set(["technician", "surgeon"]);

const COVENANT_POINTS = [
    {
        id: "technical_mass",
        title: "Technical Mass",
        descriptor:
            "Maintain hands-on proficiency across at least one strike vector — offensive security, cloud, or backend. Bench-test before every engagement.",
    },
    {
        id: "trinity_boarding",
        title: "Trinity Boarding",
        descriptor:
            "Complete all three boarding gates — Director Interview, $250 First Light fee, and Skills Intake — before claiming an active mission.",
    },
    {
        id: "ledger_oath",
        title: "60 / 30 / 10 LAW",
        descriptor:
            "Honor the LAW: 60% to the placing recruiter, 30% to the platform, 10% to the CyberKids anchor. No side-deals. No skim.",
    },
    {
        id: "velocity_48h",
        title: "48-Hour Velocity",
        descriptor:
            "Acknowledge new mission assignments within 48 hours. Silent runs are grounds for revocation of clearance.",
    },
    {
        id: "steel_box",
        title: "Steel Box Maintenance",
        descriptor:
            "Operate from a hardened, encrypted workstation. Patch monthly. No client data on personal devices, ever.",
    },
];

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const { search } = useLocation();
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({ name: "", email: "", password: "", specialized_role: "" });
    const [covenantChecks, setCovenantChecks] = useState(() =>
        Object.fromEntries(COVENANT_POINTS.map((p) => [p.id, false]))
    );
    const [loading, setLoading] = useState(false);

    const requiresCovenant = COVENANT_REQUIRED_ROLES.has(form.specialized_role);
    const totalSteps = requiresCovenant ? 3 : 2;
    const allCovenantChecked = COVENANT_POINTS.every((p) => covenantChecks[p.id]);

    function goNextFromBasics(e) {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || form.password.length < 8) {
            toast.error("Complete name, email, and a password (8+ chars with a letter and a digit).");
            return;
        }
        setStep(2);
    }

    async function submitRoleStep(e) {
        e.preventDefault();
        if (!form.specialized_role) {
            toast.error("Select a Specialized Role to continue.");
            return;
        }
        if (COVENANT_REQUIRED_ROLES.has(form.specialized_role)) {
            setStep(3);
            return;
        }
        await finalizeRegistration(false);
    }

    async function submitCovenantStep(e) {
        e.preventDefault();
        if (!allCovenantChecked) {
            toast.error("All five covenant points must be acknowledged.");
            return;
        }
        await finalizeRegistration(true);
    }

    async function finalizeRegistration(covenant_accepted) {
        setLoading(true);
        try {
            await register(form.email, form.password, form.name, form.specialized_role, covenant_accepted);
            toast.success(
                covenant_accepted
                    ? "Welcome to Sovereign. Covenant logged."
                    : "Welcome to Sovereign. Role mandate logged."
            );
            navigate({ pathname: "/dashboard", search });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div data-testid="register-page" className="mx-auto flex min-h-[80vh] max-w-2xl flex-col justify-center px-6 py-16">
            <div className="caption text-[var(--brand-accent)]">
                Apply to Recruit · Step {step} of {totalSteps}
            </div>
            <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA]">
                {step === 1 && "Create your account"}
                {step === 2 && "Choose your Specialized Role"}
                {step === 3 && "The Technical Covenant"}
            </h1>
            <p className="mt-3 text-sm text-[#A3A3A3]">
                {step === 1 && "Submit candidates · earn $10–25k bounties."}
                {step === 2 && "Mandatory selection. New boarders are categorized for President Kirk's review."}
                {step === 3 && "Operators must acknowledge all five points before reaching the Director's queue."}
            </p>

            {step === 1 && (
                <form onSubmit={goNextFromBasics} className="mt-10 space-y-5">
                    <Field label="Full name">
                        <input
                            data-testid="input-name"
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] focus:border-[var(--brand-accent)] focus:outline-none"
                        />
                    </Field>
                    <Field label="Email">
                        <input
                            data-testid="input-email"
                            type="email"
                            required
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] focus:border-[var(--brand-accent)] focus:outline-none"
                        />
                    </Field>
                    <Field label="Password (min 8 chars · letter + digit)">
                        <input
                            data-testid="input-password"
                            type="password"
                            required
                            minLength={8}
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className="w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] focus:border-[var(--brand-accent)] focus:outline-none"
                        />
                    </Field>
                    <button
                        data-testid="btn-register-next"
                        type="submit"
                        className="w-full rounded-sm py-3.5 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(201,169,110,0.35)]"
                        style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                    >
                        Continue → Role Mandate
                    </button>
                </form>
            )}

            {step === 2 && (
                <form onSubmit={submitRoleStep} className="mt-10 space-y-6">
                    <div data-testid="role-mandate" className="space-y-3">
                        {SPECIALIZED_ROLES.map((r) => {
                            const selected = form.specialized_role === r.id;
                            return (
                                <button
                                    key={r.id}
                                    type="button"
                                    data-testid={`role-option-${r.id}`}
                                    onClick={() => setForm({ ...form, specialized_role: r.id })}
                                    className={`block w-full rounded-sm border px-5 py-5 text-left transition-all duration-200 ${
                                        selected
                                            ? "border-[var(--brand-accent)] bg-[rgba(201,169,110,0.06)] shadow-[0_0_24px_rgba(201,169,110,0.18)]"
                                            : "border-white/10 bg-white/[0.02] hover:border-white/25"
                                    }`}
                                >
                                    <div className="flex items-baseline justify-between gap-4">
                                        <div className="font-serif text-2xl text-[#FAFAFA]">{r.title}</div>
                                        <div className="caption text-[var(--brand-accent)]">{r.tagline}</div>
                                    </div>
                                    <p className="mt-2 text-sm text-[#A3A3A3]">{r.descriptor}</p>
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex gap-3">
                        <button
                            data-testid="btn-register-back-to-basics"
                            type="button"
                            onClick={() => setStep(1)}
                            className="rounded-sm border border-white/15 px-5 py-3.5 text-sm text-[#FAFAFA] hover:bg-white/[0.04]"
                        >
                            ← Back
                        </button>
                        <button
                            data-testid="btn-register"
                            type="submit"
                            disabled={loading || !form.specialized_role}
                            className="flex-1 rounded-sm py-3.5 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(201,169,110,0.35)] disabled:opacity-50"
                            style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                        >
                            {loading
                                ? "Boarding…"
                                : requiresCovenant
                                    ? "Continue → Technical Covenant"
                                    : "Confirm role & create account"}
                        </button>
                    </div>
                </form>
            )}

            {step === 3 && (
                <form onSubmit={submitCovenantStep} className="mt-10 space-y-6">
                    <div data-testid="covenant-points" className="space-y-3">
                        {COVENANT_POINTS.map((p, idx) => {
                            const checked = !!covenantChecks[p.id];
                            return (
                                <label
                                    key={p.id}
                                    data-testid={`covenant-row-${p.id}`}
                                    className={`flex cursor-pointer gap-4 rounded-sm border px-5 py-4 transition-all duration-200 ${
                                        checked
                                            ? "border-[var(--brand-accent)] bg-[rgba(201,169,110,0.06)]"
                                            : "border-white/10 bg-white/[0.02] hover:border-white/25"
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        data-testid={`covenant-check-${p.id}`}
                                        checked={checked}
                                        onChange={(e) =>
                                            setCovenantChecks((prev) => ({ ...prev, [p.id]: e.target.checked }))
                                        }
                                        className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[var(--brand-accent)]"
                                    />
                                    <div className="flex-1">
                                        <div className="flex items-baseline gap-3">
                                            <span className="caption text-[var(--brand-accent)]">
                                                {String(idx + 1).padStart(2, "0")}
                                            </span>
                                            <div className="font-serif text-xl text-[#FAFAFA]">{p.title}</div>
                                        </div>
                                        <p className="mt-1.5 text-sm text-[#A3A3A3]">{p.descriptor}</p>
                                    </div>
                                </label>
                            );
                        })}
                    </div>

                    <div className="flex gap-3">
                        <button
                            data-testid="btn-register-back-to-role"
                            type="button"
                            onClick={() => setStep(2)}
                            className="rounded-sm border border-white/15 px-5 py-3.5 text-sm text-[#FAFAFA] hover:bg-white/[0.04]"
                        >
                            ← Back
                        </button>
                        <button
                            data-testid="btn-covenant-submit"
                            type="submit"
                            disabled={loading || !allCovenantChecked}
                            className="flex-1 rounded-sm py-3.5 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(201,169,110,0.35)] disabled:opacity-50"
                            style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                        >
                            {loading ? "Boarding…" : "Sign covenant & create account"}
                        </button>
                    </div>
                </form>
            )}

            <p className="mt-8 text-sm text-[#A3A3A3]">
                Already have one?{" "}
                <Link to={{ pathname: "/login", search }} className="text-[var(--brand-accent)] hover:underline" data-testid="link-login">
                    Sign in
                </Link>
            </p>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="caption text-[#737373]">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
